import { AbandonedVehicleReviewStatus, AbandonedVehicleStatus } from "./api/Network";
import { Match } from "./loaders/abandonedVehicleLoader";
import { AbandonedVehicleCreateReq, AerialImage } from "./api/Application";
import { latLngToPixel, Pixel, PixelOffset } from "./common/Geography";
import { Obb } from "./common/Geometry";
import { LayerProps, TextLayer, TileLayer, TileLayerProps } from "deck.gl";
import { LanczosBitmapLayer } from "./components/LanczosBitmapLayer";
import { aerialImageTileSpec } from "./api/Url";
import { linearInterpolate } from "./common/Numeric";

const teal:   [number, number, number, number] = [190, 225, 222, 255];
const purple: [number, number, number, number] = [213, 200, 222, 255];
const pink:   [number, number, number, number] = [233, 187, 186, 255];
const yellow: [number, number, number, number] = [242, 242, 199, 255];
const orange: [number, number, number, number] = [235, 219, 187, 255];

type Color4 = [number, number, number, number];

interface Area {
    id:    number;
    code:  string;
    name:  string;
    color: Color4;
}

export const areaCodes: Area[] = [
    { id: 1,  code: "Ca",  name: "Cabbage Hill",              color: purple },
    { id: 2,  code: "CBD", name: "Central Business District", color: pink   },
    { id: 3,  code: "CH",  name: "Chestnut Hill",             color: orange },
    { id: 4,  code: "Ct",  name: "Churchtowne",               color: orange },
    { id: 5,  code: "CP",  name: "College Park",              color: yellow },
    { id: 6,  code: "Co",  name: "Conestega Heights",         color: orange },
    { id: 7,  code: "ES",  name: "East Side",                 color: teal   },
    { id: 8,  code: "MP",  name: "Musser Park",               color: orange },
    { id: 9,  code: "Mt",  name: "Mussertown",                color: yellow },
    { id: 10, code: "PH",  name: "Prospect Heights",          color: yellow },
    { id: 11, code: "Ro",  name: "Ross",                      color: purple },
    { id: 12, code: "SS",  name: "Southside",                 color: teal   },
    { id: 13, code: "SD",  name: "Stadium District",          color: teal   },
    { id: 14, code: "St",  name: "Stevens",                   color: purple },
    { id: 15, code: "WE",  name: "West End",                  color: pink   },
];

export function lineMidpoint( coords: number[][] ) {
    // compute cumulative distances
    let totalLen = 0;
    const lengths: number[] = [0];
    for( let i = 1; i < coords.length; i++ ) {
        const dx = coords[i][0] - coords[i - 1][0];
        const dy = coords[i][1] - coords[i - 1][1];
        totalLen += Math.sqrt( dx * dx + dy * dy );
        lengths.push( totalLen );
    }

    const halfLen = totalLen / 2;
    // find the segment that contains the midpoint
    for( let i = 1; i < lengths.length; i++ ) {
        if( lengths[i] >= halfLen ) {
            const segLen = lengths[i] - lengths[i - 1];
            const t = segLen > 0 ? ( halfLen - lengths[i - 1] ) / segLen : 0;
            const p0 = coords[i - 1];
            const p1 = coords[i];
            const dx = p1[0] - p0[0];
            const dy = p1[1] - p0[1];
            const angle = Math.atan2( -dy, dx ) * ( 180 / Math.PI );
            return {
                position: [
                    p0[0] + t * dx,
                    p0[1] + t * dy,
                ],
                angle: angle > 90 ? angle - 180 : angle < -90 ? angle + 180 : angle,
            };
        }
    }
    return { position: coords[0], angle: 0 };
}

/**
 * Returns scale factors sx and sy that can be used to transform 
 * a pixel from image a to image b [xa * sx, ya * sy] => [xb, yb] + some offset
 */
export function getImageScale( a: AerialImage, b: AerialImage ): [number, number] {
    const sx = ( ( a.maxX - a.minX ) / a.width  ) / ( ( b.maxX - b.minX ) / b.width );
    const sy = ( ( a.maxY - a.minY ) / a.height ) / ( ( b.maxY - b.minY ) / b.height );
    return [sx, sy];
}

export type DeckColor = [number, number, number, number];
export function getStatusColor( status: AbandonedVehicleStatus ): DeckColor {
    const yellow: DeckColor = [ 255, 255,   0, 255 ];
    const gray  : DeckColor = [ 255, 128,  52, 255 ];
    const green : DeckColor = [   0, 255,   0, 255 ];
    const red   : DeckColor = [ 255,   0,   0, 255 ];
    const cyan  : DeckColor = [ 100, 100, 255, 255 ];
    switch( status ) {
        case AbandonedVehicleStatus.Candidate:                   return yellow;
        case AbandonedVehicleStatus.ReviewedUnsure:              return gray;
        case AbandonedVehicleStatus.ReviewedVerified:            return green;
        case AbandonedVehicleStatus.ReviewedRejected:            return red;
        case AbandonedVehicleStatus.ReviewedRejectedIdentity:    return red;
        case AbandonedVehicleStatus.ReviewedRejectedMoved:       return red;
        case AbandonedVehicleStatus.ReviewedRejectedNotAVehicle: return red;
        case AbandonedVehicleStatus.ReviewedRejectedArrived:     return red;
        case AbandonedVehicleStatus.ReviewedRejectedDeparted:    return red;
        case AbandonedVehicleStatus.InPersonVerified:            return cyan;
        default:
            throw new Error( "Invalid AbandonedVehicleStatus!" );
    }
}

export function getReviewStatusColor( status: AbandonedVehicleReviewStatus ): DeckColor {
    const orange: DeckColor = [ 255, 128,   0, 255 ];
    const gray:   DeckColor = [ 128, 128, 128, 255 ];
    const yellow: DeckColor = [ 255, 255,   0, 255 ];        
    const red:    DeckColor = [ 255,   0,   0, 255 ];
    const cyan:   DeckColor = [  0,  200, 200, 255 ];
    const green:  DeckColor = [   0, 255,   0, 255 ];

    switch( status ) {
        case AbandonedVehicleReviewStatus.New:           return cyan;
        case AbandonedVehicleReviewStatus.NotActionable: return green;
        case AbandonedVehicleReviewStatus.Actionable:    return red;
        default:
            throw new Error( "Invalid AbandonedVehicleReviewStatus!" );
    }
}

function transformGeometry( geometry: any, image: AerialImage ): any {
    if( geometry.type === "GeometryCollection" ) {
        return {
            ...geometry,
            geometries: geometry.geometries.map( ( g: any ) => transformGeometry( g, image ) ),
        };
    }
    return {
        ...geometry,
        coordinates: transformCoords( geometry.coordinates, image ),
    };
}

export function transformGeoJsonToPixel( geojson: any, image: AerialImage ) {
    return {
        ...geojson,
        features: geojson.features.map( ( f: any ) => ( {
            ...f,
            geometry: transformGeometry( f.geometry, image ),
        } ) ),
    };
}

function transformCoords( coords: any, image: AerialImage ): any {
    if( typeof coords[0] === "number" ) {
        const [lng, lat] = coords;
        const [px, py] = latLngToPixel( lat, lng, image );
        return [px, py];
    }
    return coords.map( ( c: any ) => transformCoords( c, image ) );
}

export function createObb( rear: Pixel, front: Pixel, width: number ): Obb {
    return {
        x:       ( rear[0] + front[0] ) / 2,
        y:       ( rear[1] + front[1] ) / 2,
        width:   Math.sqrt( ( front[0] - rear[0] ) ** 2 + ( front[1] - rear[1] ) ** 2 ),
        height:  width,
        angle:   Math.atan2( front[1] - rear[1], front[0] - rear[0] )
    }
}

export function createReq( runId: number, obb: Obb ): AbandonedVehicleCreateReq {
    return {
        runId:  runId,
        x:      obb.x,
        y:      obb.y,
        width:  obb.width,
        height: obb.height,
        angle:  obb.angle
    };
}

export function getCallToAction( reviewStatus: AbandonedVehicleReviewStatus | undefined ) {
    if( reviewStatus === AbandonedVehicleReviewStatus.New ) {
        return "Need Attention";
    }
    if( reviewStatus === AbandonedVehicleReviewStatus.NotActionable ) {
        return "Vehicles Allowed";
    }
    if( reviewStatus === AbandonedVehicleReviewStatus.Actionable ) {
        return "Awaiting Enforcement"
    }
    return "Error";
}

interface AerialImageReference {
    offset:    PixelOffset;
    baseImage: AerialImage;
}

interface AerialTileLayerOpts {
    id:            string;
    image:         AerialImage;
    imageRef?:     AerialImageReference;
    visible?:      boolean;
    opacity:       number;
    lanczosWeight: number;
    onEnd?:        () => void;
}

export function createAerialTileLayer( { id, image, imageRef, visible = true, opacity, lanczosWeight, onEnd = () => {} }: AerialTileLayerOpts ) {    
    let modelMatrixObj: Pick<LayerProps, "modelMatrix"> = {};
    if( imageRef ) {
        const { offset, baseImage } = imageRef;

        //get appropriate GSD scale so images can be aligned
        const [scaleX, scaleY] = getImageScale( image, baseImage );

        //since we are moving A to match B we negate the stored B -> A offsets
        const [offsetX, offsetY] = [-offset[0], -offset[1]];
        modelMatrixObj = {
            modelMatrix: [
                scaleX,  0,       0, 0,
                0,       scaleY,  0, 0,
                0,       0,       1, 0,
                offsetX, offsetY, 0, 1
        ] };
    }
    
    return new TileLayer<ImageBitmap>( {
        id,
        visible,
        opacity,
        ...modelMatrixObj,
        pickable:           false,
        tileSize:           512,
        refinementStrategy: "no-overlap",
        minZoom:            -3,
        maxZoom:            0,
        extent:             [0, 0, image.width, image.height],
        data:               aerialImageTileSpec( image.aerialImageId ),
        updateTriggers: {
            renderSubLayers: [lanczosWeight]
        },
        renderSubLayers: props => {
            const [[left, bottom], [right, top]] = props.tile.boundingBox;
            const { data, ...otherProps } = props;
            return new LanczosBitmapLayer( otherProps, {
                weight: lanczosWeight,
                image:  data,
                transitions: {
                    opacity: { duration: 150 },
                    onEnd: onEnd
                },
                bounds: [left, top, right, bottom],
                textureParameters: {
                    minFilter: "nearest",
                    magFilter: "nearest"
                }
            } );
        },
    } );
}

export interface RoadLabelData { name: string; position: Pixel; angle: number; };
interface RoadLabelsOpts {
    labelData: RoadLabelData[];
    zoom:      number;
}

export function createRoadLabelsLayer( { labelData, zoom }: RoadLabelsOpts ): any {
    const roadOpac = linearInterpolate( -1, 1, 0, 0.75, zoom );    
    return new TextLayer<RoadLabelData>( {
        id: "road-labels",
        data: labelData,
        getText: f => f.name,
        billboard: true,
        opacity: roadOpac,
        getPosition: f => {
            const [x, y] = f.position;
            const x1 = x;
            const y1 = y - 11;
            return [x1, y1];
        },
        getAngle: d => d.angle, // TextLayer angles are counter-clockwise
        sizeUnits: "pixels",
        getSize:   14,
        getColor:  [255, 255, 255],
        getTextAnchor: "middle",
        getAlignmentBaseline: "center",
        coordinateSystem: "cartesian",
    } );
}

export function hilbertD( order: number, x: number, y: number ): number {
    let rx = 0, ry = 0, d = 0;
    for( let s = ( 1 << order ) >>> 1; s > 0; s >>>= 1 ) {
        rx = ( x & s ) > 0 ? 1 : 0;
        ry = ( y & s ) > 0 ? 1 : 0;
        d += s * s * ( ( 3 * rx ) ^ ry );
        // rotate quadrant
        if( ry === 0 ) {
            if( rx === 1 ) { x = s - 1 - x; y = s - 1 - y; }
            [x, y] = [y, x];
        }
    }
    return d;
}